import { Button } from '@/components/ui-kit/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useGetOwnTemplatesForOrg } from '@/modules/templates/hooks/use-templates';
import { useCurrentOrgId } from '@/store/use-my-oganization-store';
import {
  Clock01Icon,
  FileValidationIcon,
  LicenseIcon,
  UserGroupIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowUpRight, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGetOrganizationByOrgId } from '@/graphql/queries/organizations';
import { Badge } from '@/components/ui-kit/badge';

const OrgOverviewPage = () => {
  const { t } = useTranslation();
  const { orgId: slug } = useParams<{ orgId: string }>();
  const orgId = useCurrentOrgId();
  const { data: org, isLoading: orgLoading } = useGetOrganizationByOrgId(slug);
  const { data: templates, isLoading: templatesLoading } = useGetOwnTemplatesForOrg(orgId);
  if (orgLoading || templatesLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">{t('ORGANIZATION_NOT_FOUND')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[hsl(var(--fg))] font-nunito">
            {t('ORGOVERVIEW_WELCOMETITLE', { orgName: org.Name })}
          </h1>
          <p className=" mt-4 font-nunito">{t('ORGOVERVIEW_WELCOMESUBTITLE')}</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        <Card className="border-y-0 border-r-0 border-[#6DAEFF] border-l-5 md:py-4 gap-4 shadow-md hover:shadow-lg transition-all duration-300">
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2 p-3!">
            <HugeiconsIcon icon={LicenseIcon} size={20} className="size-6 text-globe-grey" />
            <CardTitle className="text-sm font-semibold font-nunito text-secondary-400! ">
              {t('ORGOVERVIEW_STATS_TOTALTEMPLATES')}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-row items-center justify-between pt-0! p-3!">
            <div>
              <p className="text-xl md:text-3xl font-semibold">{templates?.length ?? 0}</p>
            </div>

            <Button variant="ghost" size="icon">
              <ArrowUpRight className="size-5 md:size-7 text-globe-grey" />
            </Button>
          </CardContent>
        </Card>

        <Card className="border-y-0 border-r-0 border-[#9A7CEF] border-l-5 md:py-4 gap-4 shadow-md hover:shadow-lg transition-all duration-300">
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2 p-3! ">
            <HugeiconsIcon icon={UserGroupIcon} size={20} className="size-6 text-globe-grey" />
            <CardTitle className="text-sm font-semibold font-nunito text-secondary-400! ">
              {t('ORGOVERVIEW_STATS_TEAMLIMIT')}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-row items-center justify-between pt-0! p-3!">
            <div>
              <p className="text-xl md:text-3xl font-semibold">{org.MaxUsers}</p>
            </div>

            <Button variant="ghost" size="icon">
              <ArrowUpRight className="size-5 md:size-7 text-globe-grey" />
            </Button>
          </CardContent>
        </Card>

        <Card className="border-y-0 border-r-0 border-[#FF7803] border-l-5 md:py-4 gap-4 shadow-md hover:shadow-lg transition-all duration-300">
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2 p-3! ">
            <HugeiconsIcon icon={Clock01Icon} size={20} className="size-6 text-globe-grey" />
            <CardTitle className="text-sm font-semibold font-nunito text-secondary-400! ">
              {t('ORGOVERVIEW_STATS_CREATED')}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-row items-center justify-between pt-0! p-3!">
            <div>
              <p className="text-xl md:text-3xl font-semibold">
                {new Date(org.CreatedDate ?? '').toLocaleDateString('en-US', {
                  month: 'short',
                  year: 'numeric',
                })}
              </p>
            </div>

            <Button variant="ghost" size="icon">
              <ArrowUpRight className="size-5 md:size-7 text-globe-grey" />
            </Button>
          </CardContent>
        </Card>

        <Card className="border-y-0 border-r-0 border-[#00A671] border-l-5 md:py-4 gap-4 shadow-md hover:shadow-lg transition-all duration-300">
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2 p-3! ">
            <HugeiconsIcon icon={FileValidationIcon} size={20} className="size-6 text-globe-grey" />
            <CardTitle className="text-sm font-semibold font-nunito text-secondary-400! ">
              {/* {t("stats.created")} */}
              Active Templates
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-row items-center justify-between pt-0! p-3!">
            <div>
              <p className="text-xl md:text-3xl font-semibold">{36}</p>
            </div>

            <Button variant="ghost" size="icon">
              <ArrowUpRight className="size-5 md:size-7 text-globe-grey" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Templates */}
      <Card>
        <CardHeader>
          <CardTitle>{t('ORGOVERVIEW_RECENTTEMPLATES_TITLE')}</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {!templates?.length ? (
            <p className="text-globe-grey text-sm">{t('ORGOVERVIEW_RECENTTEMPLATES_EMPTY')}</p>
          ) : (
            <div className="space-y-3">
              {templates.slice(0, 10).map((template) => (
                <div
                  key={template.id}
                  className="flex items-center justify-between py-2 border-b border-border last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-globe-grey" />
                    <div>
                      <p className="font-medium text-sm">{template.title}</p>
                      <p className="text-xs text-globe-grey">{template.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={template.available ? 'success' : 'secondary'}>
                      {template.available
                        ? t('ORGOVERVIEW_RECENTTEMPLATES_STATUSACTIVE')
                        : t('ORGOVERVIEW_RECENTTEMPLATES_STATUSDRAFT')}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrgOverviewPage;
